<template>
    <v-form ref="form" v-model="valid" :disabled="disabled">
        <v-row class="mt-4" dense>
            <v-col cols="12" md="2">
                <v-select
                    v-model="info.type"
                    item-title="name"
                    item-value="value"
                    variant="outlined"
                    hide-details
                    :items="types"
                    :label="`${$t('type')}*`"
                    :rules="[requiredRule('type')]"
                />
            </v-col>
            <v-col cols="12" md="3">
                <v-autocomplete
                    v-model="info.assignedToId"
                    :item-title="userName"
                    item-value="personId"
                    hide-details
                    variant="outlined"
                    :items="users"
                    :label="`${$t('assigned_to')}*`"
                    :loading="usersLoading"
                    :disabled="usersLoading"
                    :rules="[requiredRule('assigned_to')]"
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-select
                    v-model="info.stage"
                    item-title="name"
                    item-value="value"
                    hide-details
                    variant="outlined"
                    :items="stages"
                    :label="`${$t('stage')}*`"
                    :rules="[requiredRule('stage')]"
                />
            </v-col>
            <v-col :md="2">
                <date-picker v-model="date" outlined dense :label="`${$t('date')}*`" :rules="[requiredRule('date')]" />
            </v-col>
            <v-col :md="2">
                <time-field v-model="time" :label="`${$t('time')}*`" />
            </v-col>
            <v-col :md="1">
                <v-switch v-model="info.active" :label="$t('active')" color="primary" class="my-0" hide-details />
            </v-col>
        </v-row>
        <v-row v-if="info.type === 'EMAIL'" dense>
            <v-col cols="12" md="6">
                <emails-field v-model="info.recipientFromMails" :label="`${$t('email_form.from')}*`" required />
            </v-col>
            <v-col cols="12" md="6">
                <emails-field v-model="info.recipientToMails" :label="`${$t('email_form.to')}*`" required />
            </v-col>
            <v-col cols="12" md="6">
                <emails-field v-model="info.recipientCCMails" :label="$t('email_form.cc')" />
            </v-col>
            <v-col cols="12" md="6">
                <emails-field v-model="info.recipientBCCMails" :label="$t('email_form.bcc')" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12">
                <v-text-field
                    v-model="info.subject"
                    variant="outlined"
                    :label="`${$t('subject')}*`"
                    persistent-placeholder
                    :rules="[requiredRule('subject')]"
                />
            </v-col>
        </v-row>
        <div class="text-h6 mb-4">{{ $t('relates_to') }}</div>
        <v-row dense>
            <v-col cols="12" md="4">
                <private-client-and-companies-autocomplete
                    ref="accountAutocomplete"
                    v-model="account"
                    :show-icon="false"
                    :label="`${$t('account')}*`"
                    outlined
                    disable-persistent-placeholder
                    :single-line="false"
                    :disabled="accountLoading"
                    :base-loading="accountLoading"
                    :rules="[requiredRule('account')]"
                    :relationship="['CLIENT', 'SUPPLIER']"
                    :filter="item => item.type !== 'TOUR_OPERATOR_1LEVEL'"
                    :no-filter="false"
                    communications-mode
                    @change="setAccount"
                />
            </v-col>
            <v-col cols="12" md="4">
                <v-select
                    v-model="info.contactId"
                    :item-title="userName"
                    item-value="personId"
                    :menu-props="{bottom: true, offsetY: true}"
                    hide-details
                    variant="outlined"
                    clearable
                    :items="contactPersons"
                    :label="$t('contact_person')"
                    :loading="contactPersonsLoading"
                    :disabled="contactPersonsDisabled || contactPersonsLoading"
                />
            </v-col>
            <v-col cols="12" md="2">
                <v-text-field
                    v-model="info.orderId"
                    type="number"
                    variant="outlined"
                    :label="$t('order_number')"
                    persistent-placeholder
                    :disabled="!!services"
                    @update:model-value="onOrderIdChange"
                />
                <v-checkbox v-if="info.type === 'EMAIL' && !info.orderId" v-model="_createNewOrder" density="compact">
                    <template v-slot:label>
                        <span class="text-caption">{{ $t('create_new_order') }}</span>
                    </template>
                </v-checkbox>
            </v-col>
            <v-col cols="12" md="2">
                <v-select
                    v-model="info.processId"
                    item-title="serviceName"
                    item-value="processId"
                    :menu-props="{bottom: true, offsetY: true}"
                    hide-details
                    variant="outlined"
                    :items="orderServices"
                    :label="$t('service')"
                    :loading="orderServicesLoading"
                    :disabled="orderServicesLoading || !info.orderId"
                    clearable
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12">
                <v-textarea
                    v-model="info.description"
                    variant="outlined"
                    :label="$t('details')"
                    persistent-placeholder
                />
            </v-col>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Model, Watch} from 'vue-facing-decorator'
    import TimeField from '@/components/snippets/forms/TimeField.vue'

    import PrivateClientAndCompaniesAutocomplete from '@/components/search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {CompanyTypeToCommunicationInfoType} from '@/utils/api-data-mapings'
    import EmailsField from '~/components/snippets/forms/EmailsField.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import type {VForm} from 'vuetify/components'

    @Component({
        components: {EmailsField, PrivateClientAndCompaniesAutocomplete, TimeField, DatePicker},
    })
    export class CommunicationEditForm extends FormValidationRulesMixin {
        @VModel() info
        @Model({name: 'createNewOrder', default: false}) _createNewOrder
        @Prop() services
        @Prop() _valid
        @Prop({default: false, type: Boolean}) isCreateMode

        usersLoading = true
        users = []
        orderServicesLoading = false
        orderServices = []
        account = null
        accountLoading = true
        contactPersons = []
        contactPersonsLoading = false
        contactPersonsDisabled = true

        @Emit()
        changeValid(val) {
            return val
        }

        @Watch('info.type')
        onTypeChange() {
            delete this.info.recipientFromMails
            delete this.info.recipientToMails
            delete this.info.recipientCCMails
            delete this.info.recipientBCCMails

            if (this.info.type === 'EMAIL') {
                this.info = {
                    ...this.info,
                    recipientFromMails: [],
                    recipientToMails: [],
                    recipientCCMails: [],
                    recipientBCCMails: [],
                }
            }
        }

        async validate() {
            return await (this.$refs.form as VForm).validate()
        }

        async mounted() {
            if (!this.info.communicationDateTime) {
                this.info.communicationDateTime = this.$dateFns.format(new Date(), "yyyy-MM-dd'T'HH:mm")
            }
            if (!this.info.type) {
                this.info.type = this.types[0].value
            }
            if (!this.info.stage) {
                this.info.stage = this.stages[0].value
            }
            try {
                this.usersLoading = true
                this.users = (await this.$api.persons.get({companyId: authStore.company.id})).users
                this.users.sort((a, b) =>
                    `${a.lastName || a.lastNameLocalized || ''}`.localeCompare(
                        `${b.lastName || b.lastNameLocalized || ''}`
                    )
                )
                if (!this.info.communicationId) {
                    const user = this.users?.find(user => user.personId === authStore.person.personId)
                    this.info.assignedToId = user?.personId || this.users[0].personId
                }
            } catch (e) {
                this.users = []
            } finally {
                this.usersLoading = false
            }
            if (this.services) {
                this.orderServices = this.services
            } else if (this.info.orderId) {
                await this.loadOrderServices(this.info.orderId)
            }
            if (this.info.accountId) {
                try {
                    this.accountLoading = true
                    if (this.info.accountType === 'PRIVATE_CLIENT') {
                        const person = (await this.$api.privateClients.get({id: this.info.accountId})).persons[0]
                        const personAdapted = {...person, id: person.personId}
                        this.$refs.accountAutocomplete.entries = [personAdapted]
                        this.account = personAdapted
                    } else {
                        const company = (await this.$api.companies.get({id: this.info.accountId})).companies[0]
                        this.$refs.accountAutocomplete.entries = [company]
                        this.account = company
                    }
                } catch (e) {
                    this.account = null
                } finally {
                    await this.setAccount()
                    this.accountLoading = false
                }
                await this.loadContactPersons()
            } else {
                this.accountLoading = false
            }
        }

        userName({firstName, lastName, firstNameLocalized, lastNameLocalized}) {
            if (!firstName && !lastName) {
                return `${firstNameLocalized || ''} ${lastNameLocalized || ''}`
            }

            return `${firstName || ''} ${lastName || ''}`
        }

        async onOrderIdChange(orderId) {
            this.info.processId = null
            this._createNewOrder = false
            await this.loadOrderServices(orderId)
        }

        async loadOrderServices(orderId) {
            if (!orderId) {
                this.orderServices = []
                return
            }
            try {
                this.orderServicesLoading = true
                this.orderServices = (
                    await this.$api.orders.get({
                        orderType: authStore.orderType,
                        orderId: this.info.orderId,
                    })
                ).orders[0].services
            } catch (e) {
                this.orderServices = []
            } finally {
                this.orderServicesLoading = false
            }
        }

        async loadContactPersons() {
            try {
                this.contactPersonsLoading = true
                this.contactPersonsDisabled = true
                this.contactPersons = (await this.$api.persons.get({companyId: this.info.accountId})).users
                this.contactPersons = this.contactPersons.sort((a, b) =>
                    `${a.lastName || a.lastNameLocalized || ''}`.localeCompare(
                        `${b.lastName || b.lastNameLocalized || ''}`
                    )
                )
            } catch (e) {
                this.contactPersons = []
            } finally {
                this.contactPersonsLoading = false
                this.contactPersonsDisabled = false
            }
        }

        async setAccount() {
            if (this.account) {
                this.info.accountId = this.account.personId || this.account.id
                this.info.accountType = this.account.personId
                    ? 'PRIVATE_CLIENT'
                    : CompanyTypeToCommunicationInfoType(this.account.type)
                if (this.account.personId) {
                    this.info.contactId = null
                    this.contactPersonsDisabled = true
                } else {
                    await this.loadContactPersons()
                }
            } else {
                this.info.accountId = null
                this.info.accountType = null
                this.info.contactId = null
                this.contactPersonsDisabled = true
            }
        }

        get types() {
            return [
                {
                    name: this.$t('communicationType.CALL'),
                    value: 'CALL',
                },
                {
                    name: this.$t('communicationType.EMAIL'),
                    value: 'EMAIL',
                },
                {
                    name: this.$t('communicationType.MEETING'),
                    value: 'MEETING',
                },
                {
                    name: this.$t('communicationType.OTHER'),
                    value: 'OTHER',
                },
            ]
        }

        get stages() {
            return [
                {
                    name: this.$t('communicationStage.PLANNED'),
                    value: 'PLANNED',
                },
                {
                    name: this.$t('communicationStage.HELD'),
                    value: 'HELD',
                },
                {
                    name: this.$t('communicationStage.NOT_HELD'),
                    value: 'NOT_HELD',
                },
            ]
        }

        get date() {
            return this.info.communicationDateTime?.split('T')[0]
        }

        set date(date) {
            this.info.communicationDateTime = `${date}T${this.info.communicationDateTime?.split('T')[1]}`
        }

        get time() {
            return this.info.communicationDateTime?.split('T')[1]
        }

        set time(time) {
            this.info.communicationDateTime = `${this.info.communicationDateTime?.split('T')[0]}T${time}`
        }

        get companyTypeToCommunicationInfoType() {
            return CompanyTypeToCommunicationInfoType
        }

        get disabled() {
            return (
                !this.isCreateMode && this.info.type === 'EMAIL' && ['SYSTEM', 'TO1L'].includes(this.info.accountType)
            )
        }

        get formattedDate() {
            return this.date !== null ? this.$filters.dateShortFormat(this.date) : ''
        }

        get valid() {
            return this._valid
        }

        set valid(val) {
            this.changeValid(val)
        }
    }

    export default CommunicationEditForm
</script>
